import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ScrollableCellWithAvatar } from '@/components/data-table/cells';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/modules/common/components/molecules/date/date-picker';
import { DateRangePicker } from '@/modules/common/components/molecules/date/date-range-picker';
import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select/single-select';

import { BillDetails } from '../../types';
import { UpdateBillFormValues } from '../../validations';

interface BillGeneralInformationCardProps {
  details: BillDetails;
  form: UseFormReturn<UpdateBillFormValues>;
  isReadOnly: boolean;
  currencyOptions: { value: string; label: string }[];
  isLoadingCurrencies: boolean;
}

/** Displays editable bill metadata independently from line-item editing. */
export function BillGeneralInformationCard({
  details,
  form,
  isReadOnly,
  currencyOptions,
  isLoadingCurrencies,
}: BillGeneralInformationCardProps) {
  const { t } = useTranslation('authenticated.bills');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('details.generalInformation')}</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {/* Team member field */}
        <Label className="flex flex-col items-stretch gap-2">
          <span>{t('details.fields.member')}</span>
          <div className="flex min-h-10 items-center rounded-md border border-input bg-muted px-3 py-1 text-sm">
            <ScrollableCellWithAvatar
              text={details.member.name}
              image={details.member.profileImageUrl}
              avatarName={details.member.name}
              avatarSize="sm"
              alt={details.member.name}
              withAvatarBorder
              className="w-full"
            />
          </div>
        </Label>
        {/* Billing period selector */}
        <DateRangePicker
          name="period"
          control={form.control}
          label={t('filters.billingPeriod')}
          disabled={isReadOnly}
        />
        {/* Due date selector */}
        <DatePicker
          name="dueDate"
          control={form.control}
          label={t('details.fields.dueDate')}
          inputDisabled={isReadOnly}
          captionLayout="dropdown"
        />
        {/* Bill currency selector */}
        <SingleSelect
          name="currencyCode"
          control={form.control}
          label={t('details.fields.currency')}
          placeholder={t('details.edit.placeholders.currency')}
          options={currencyOptions}
          disabled={isReadOnly}
          loading={isLoadingCurrencies}
        />
      </CardContent>
    </Card>
  );
}
